/**
 * Shared utilities for video platform components
 */

import { format, parseISO } from "date-fns";

/** Forward window (days) for the default upcoming listing. */
const UPCOMING_WINDOW_DAYS = 30;

/**
 * Builds the meetings query for a given status filter.
 *
 * - "ALL": nearest-first window from local midnight → +30d, prioritizing
 *   ACTIVE. Starting at midnight (not "now") keeps today's already-Ended
 *   sessions visible — e.g. a continuous room's morning slots stay listed
 *   under their group for the rest of the day. No excludeStatus is sent.
 * - A specific status (e.g. "ENDED"): server-side status query, most-recent
 *   first, no window — so admins can reach every ended meeting to moderate chat.
 */
export const buildMeetingQueryParams = (status: string = "ALL") => {
  if (status === "ALL") {
    const now = new Date();
    const todayStart = new Date(now);
    todayStart.setHours(0, 0, 0, 0);
    const end = new Date(
      now.getTime() + UPCOMING_WINDOW_DAYS * 24 * 60 * 60 * 1000
    );
    return {
      prioritizeStatus: "ACTIVE",
      sortBy: "date",
      sortOrder: "asc",
      startDate: todayStart.toISOString(),
      endDate: end.toISOString(),
      limit: 100,
      // Segments are needed to render the per-hour breakdown grouped under
      // each continuous room; the list collapses them client-side.
      includeContinuousSegments: true,
    };
  }

  return {
    status,
    sortBy: "date",
    sortOrder: "desc",
    limit: 100,
    includeContinuousSegments: true,
  };
};

/** "power-hour" / "community_session" → "Power Hour" / "Community Session". */
export const prettifyRoomKey = (key: string): string =>
  key
    .split(/[-_]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export const getStatusColor = (status: string) => {
  const statusUpper = status?.toUpperCase() || "UNKNOWN";
  switch (statusUpper) {
    case "ACTIVE":
      return { bgcolor: "#0bc68a", color: "#1a1918" };
    case "ENDED":
    case "CANCELLED":
      return { bgcolor: "#e4463a", color: "#ffffff" };
    case "CREATED":
      return { bgcolor: "#2196f3", color: "#ffffff" };
    case "INACTIVE":
      return { bgcolor: "#9e9e9e", color: "#ffffff" };
    default:
      return { bgcolor: "#9e9e9e", color: "#ffffff" };
  }
};

export const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";

  try {
    const date = parseISO(dateString);
    return format(date, "yyyy-MM-dd HH:mm");
  } catch {
    return "-";
  }
};
